import { useState, useRef, useEffect, useCallback } from 'react';
import { Icon, Can, Button, Spinner } from '@iq/react-components';
import { useDispatch, useSelector } from 'react-redux';

import {
  importSource,
  getImportableSourceTypes,
  getInjectableSourceTypes,
  uploadRiskConfigExcel,
  requestSources,
} from '../../../../../bundles/sources';
import { INTERNAL_DATA_SOURCE } from '../../../../../constants';
import JsonEditor from '../../../../JsonEditor';
import TypeLabel from '../../../../TypeLabel';
import StyledItem from '../../../../StyledItem';
import ConfirmationDialog from '../../../../ConfirmationDialog';
import config from '../../../../../config';

const SourceListItem = ({
  site = {},
  title,
  isOpen: isColumnOpen,
  source,
  schema,
  uiSchema,
  onDelete,
  onSubmit,
  transformErrors,
  customFormats,
  customValidate,
  isEditable = true,
}) => {
  const [isImportingVariables, setIsImportingVariables] = useState(false);
  const [isImportingRiskConfig, setIsImportingRiskConfig] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [forceCloseEditor, setForceCloseEditor] = useState(0);
  const dispatch = useDispatch();
  const [isOpen, setOpen] = useState(isColumnOpen);
  const importTypes = useSelector(getImportableSourceTypes);
  const riskAwareSourceTypes = useSelector(getInjectableSourceTypes);
  const inputReference = useRef(null);
  const updatedSource = source;

  const { onPremise } = config;

  if (source?.options?.importedFileName) {
    updatedSource.importedFileName = source.options.importedFileName;
  }
  if (source?.options?.riskStatusEnabled !== undefined) {
    updatedSource.fetchRiskStatus = source.options.riskStatusEnabled ? 'Enabled' : 'Disabled';
  }
  if (source?.options?.alarmFilteringEnabled !== undefined) {
    updatedSource.alarmFilteringEnabled = source.options.alarmFilteringEnabled
      ? 'Enabled'
      : 'Disabled';
  }
  const onImport = () => {
    setIsImportingVariables(true);
    dispatch(importSource(site.id, source.id, () => setIsImportingVariables(false)));
  };

  useEffect(() => {
    setOpen(isColumnOpen);
  }, [isColumnOpen]);

  const onUpdateSourceProperties = useCallback(
    ({ formData }) => {
      const fdCopy = { ...formData };
      if (fdCopy.options) {
        for (const optKey of Object.keys(fdCopy.options)) {
          if (
            !fdCopy.options[optKey] &&
            fdCopy.options[optKey] !== false &&
            !(schema.options?.required || []).includes(optKey)
          ) {
            delete fdCopy.options[optKey];
          }
        }
      }
      onSubmit(source.id, formData, () => setForceCloseEditor((v) => v + 1));
    },
    [onSubmit, source.id, schema.options]
  );

  const importDisabled =
    isImportingVariables ||
    (source.type === 'custom' &&
      !(source.options?.import?.endpoint && source.options?.import?.enabled));

  const fileImport = () => {
    // 👇️ open file input box on click of other element
    inputReference.current.value = '';
    inputReference.current.click();
  };

  const parseRiskConfigExcel = (event) => {
    setIsImportingRiskConfig(true);
    const fileObject = event.target.files?.[0];

    dispatch(
      uploadRiskConfigExcel(source.id, fileObject, site.id, site.org, () => {
        dispatch(requestSources(site.id));
        setIsImportingRiskConfig(false);
      })
    );
  };

  return (
    <StyledItem
      itemClass="source-list-item"
      active={isOpen}
      onClick={() => setOpen(!isOpen)}
      headerLabel={<TypeLabel text={source.type} />}
      headerContent={<div className="title">{title}</div>}
      headerActions={<Icon icon={isOpen ? 'he-up' : 'he-down'} />}
      bodyActions={
        <>
          {source.type !== INTERNAL_DATA_SOURCE && !onPremise && (
            <Can
              permission="sources/Delete"
              scope={{ org: site.org, site: site.id }}
            >
              <Button
                activity="secondary"
                onClick={() => setConfirmDelete(true)}
              >
                Delete source
              </Button>
            </Can>
          )}
        </>
      }
      bodyActionsPosition="center"
    >
      <JsonEditor
        title="Source properties"
        formData={source}
        schema={schema}
        uiSchema={uiSchema}
        initialEditMode={false}
        keepOpenOnSubmit={source.type === 'dec'}
        forceClose={forceCloseEditor}
        ignoreKeys={['options', 'variables']}
        onFormSubmit={onUpdateSourceProperties}
        saveButtonText={'Update source'}
        customTransformErrors={(errors) => transformErrors(errors)}
        customFormats={customFormats}
        customValidate={customValidate}
        showEditButton={isEditable}
      />
      {confirmDelete && (
        <ConfirmationDialog
          onCancel={() => setConfirmDelete(false)}
          onConfirm={(event) => {
            event.preventDefault();
            onDelete(source.id);
            setConfirmDelete(false);
          }}
          confirmType="danger"
          title="Remove source"
          body={
            'This action is irreversible and may affect visualizations which depend on this data source. Do you want to proceed?'
          }
          confirmText="Remove"
        />
      )}
      {importTypes.includes(source.type) && (
        <Can
          permission="sources/Write"
          scope={{ org: site.org, site: site.id }}
        >
          <Button
            onClick={onImport}
            disabled={importDisabled}
          >
            {!isImportingVariables && 'Import signals'}
            {isImportingVariables && <Spinner />}
          </Button>
        </Can>
      )}
      {riskAwareSourceTypes.includes(source.type) && (
        <>
          <input
            style={{ display: 'none' }}
            ref={inputReference}
            type="file"
            onChange={(event) => parseRiskConfigExcel(event)}
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          />
          <Can
            permission="sources/Write"
            scope={{ org: site.org, site: site.id }}
          >
            <Button
              icon={
                !isImportingRiskConfig && (
                  <Icon
                    style={{ width: '80%' }}
                    icon="he-upload"
                  />
                )
              }
              tooltip={'Load Risk Panel Configuration File'}
              activity="primary"
              onClick={() => fileImport()}
            >
              {!isImportingRiskConfig && <div className="button-text">Risk Panel Config</div>}
              {isImportingRiskConfig && <Spinner />}
            </Button>
          </Can>
        </>
      )}
    </StyledItem>
  );
};

export default SourceListItem;
